<template>
  <OnBoardingFrame :error="errorMessage">
    <!-- Same frame as Sign up with Log in selected, so only the words and the email
         button change when the hiker switches tabs. -->
    <template #nav>
      <div class="ob-seg" role="tablist">
        <button type="button" class="ob-seg__tab" role="tab" aria-selected="false" @click="switchToSignUp">
          {{ $t('on_boarding.login_page.sign_up') }}
        </button>
        <button type="button" class="ob-seg__tab ob-seg__tab--on" role="tab" aria-selected="true">
          {{ $t('shared.log_in') }}
        </button>
      </div>
    </template>

    <div class="ob-heading">
      <h2 id="onBoardingTitle" class="ob-title">{{ $t('on_boarding.login_page.welcome_back') }}</h2>
      <p class="ob-subtitle">{{ $t('on_boarding.login_page.pick_up_where_you_left_off') }}</p>
    </div>

    <div class="ob-stack">
      <button
        v-if="onBoardingIncludesGoogle"
        type="button"
        class="ob-btn ob-btn-google"
        :disabled="isLoading"
        @click="signInWithGoogle"
      >
        <img
          width="18"
          height="18"
          loading="lazy"
          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
          alt=""
        >
        {{ $t('on_boarding.login_page.continue_with_google') }}
      </button>

      <button
        v-if="onBoardingIncludesApple"
        type="button"
        class="ob-btn ob-btn-apple"
        :disabled="isLoading"
        @click="signInWithApple"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43Zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282Z"/>
      </svg>
        {{ $t('on_boarding.login_page.continue_with_apple') }}
      </button>

      <div v-if="hasSocialOption && hasEmailLikeOption" class="ob-or">{{ $t('on_boarding.login_page.text_1d00e7dc') }}</div>

      <button
        v-if="onBoardingIncludesEmail"
        type="button"
        class="ob-btn ob-btn-secondary"
        :disabled="isLoading"
        @click="signInWithEmail"
      >
        <span class="ms fill" aria-hidden="true">mail</span>
        {{ $t('on_boarding.login_page.log_in_with_email') }}
      </button>

      <button
        v-if="onBoardingIncludesMagicLink"
        type="button"
        class="ob-text-btn"
        :disabled="isLoading"
        @click="signInWithMagicLink"
      >
        <span class="ms fill" aria-hidden="true">auto_awesome</span>
        {{ $t('on_boarding.login_page.email_me_a_magic_link') }}
      </button>
    </div>

    <template #footer>
      <i18n-t keypath="on_boarding.login_page.by_continuing_you_accept_our_terms" tag="p" class="ob-legal" scope="global">
        <template #terms><a href="/terms-of-service">{{ $t('on_boarding.login_page.terms_of_service') }}</a></template>
        <template #privacy><a href="/privacy">{{ $t('on_boarding.login_page.privacy_policy') }}</a></template>
      </i18n-t>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
// eslint-disable-next-line import/extensions,import/no-unresolved
import { OnBoardingPage } from '../../../utils/models/OnBoardingPage';
import { SignInProvider } from '../../../utils/models/SignInProvider';
import { AuthMethods } from '../store/modules/OnBoardingModule';
import OnBoardingFrame from './OnBoardingFrame.vue';

export default defineComponent({
  name: 'OnBoardingLoginPage',
  components: { OnBoardingFrame },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    onBoardingIncludesEmail(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.Email);
    },
    onBoardingIncludesGoogle(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.Google);
    },
    onBoardingIncludesApple(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.Apple);
    },
    onBoardingIncludesMagicLink(): boolean {
      return this.$store.state.onBoarding.authMethods.includes(AuthMethods.MagicLink);
    },
    hasSocialOption(): boolean {
      return this.onBoardingIncludesGoogle || this.onBoardingIncludesApple;
    },
    hasEmailLikeOption(): boolean {
      return this.onBoardingIncludesEmail || this.onBoardingIncludesMagicLink;
    },
  },
  methods: {
    signInWithGoogle() {
      this.$store.dispatch('onBoarding/signInWith', SignInProvider.Google);
    },
    signInWithApple() {
      this.$store.dispatch('onBoarding/signInWith', SignInProvider.Apple);
    },
    signInWithEmail() {
      // From the login page, "email" goes straight to EmailLogin, not EmailSignUp.
      this.$store.commit('onBoarding/pushPage', OnBoardingPage.EmailLogin);
    },
    signInWithMagicLink() {
      this.$store.dispatch('onBoarding/signInWith', SignInProvider.MagicLink);
    },
    switchToSignUp() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/setPageStack', [OnBoardingPage.SignUpOptions]);
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
